import type { TFunction } from "i18next";
import { Trans, useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { useImperativeHandle, type Ref } from "react";
import * as Yup from "yup";
import type {
  IEvaluationRequestDetailScore,
  IEvaluationRequestDetailSummaryMetric,
} from "@/types/(company)/performance.interface";
import { EmojiRating } from "./review-emoji-rating";
import { TextField } from "@/components/Textfield";
import { cn } from "@/utils/cn";

export interface MetricCardRef {
  validate: () => Promise<boolean>;
  getValues: () => {
    metricId: number;
    score: number;
    reason: string;
    areasOfImprovement: string;
  };
}

interface MetricCardProps {
  score: IEvaluationRequestDetailScore;
  isReviewMode?: boolean;
  ref?: Ref<MetricCardRef>;
  metricScore: IEvaluationRequestDetailSummaryMetric | null;
  index: number;
}

/** Validation messages are UI copy, so build the schema per render. */
const buildValidationSchema = (t: TFunction) =>
  Yup.object({
    score: Yup.number()
      .min(1, t("company:performance_review.please_select_a_rating"))
      .required(t("company:performance_review.rating_required")),
    reason: Yup.string()
      .trim()
      .required(t("my-space:toasts.reason_is_required")),
    areasOfImprovement: Yup.string()
      .trim()
      .required(t("company:performance_review.area_of_improvement_required")),
  });

export const MetricCard = ({
  score,
  isReviewMode = false,
  ref,
  metricScore,
  index,
}: MetricCardProps) => {
  const { t } = useTranslation(["common", "company", "my-space"]);
  const initialValues = {
    score: score.score ?? 0,
    reason: score.reason ?? "",
    areasOfImprovement: score.areasOfImprovement ?? "",
  };

  const emojiToPercent: Record<number, number> = {
    1: 0,
    2: 25,
    3: 50,
    4: 75,
    5: 100,
  };

  const formik = useFormik({
    initialValues,
    validationSchema: isReviewMode ? buildValidationSchema(t) : undefined,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: () => {},
  });

  const calculatedWeightedScore =
    formik.values.score && formik.values.score > 0
      ? String(
          parseFloat(
            (
              (emojiToPercent[formik.values.score] / 100) *
              Number(score.weightage)
            ).toFixed(1),
          ),
        )
      : null;

  const displayScore =
    metricScore?.weightedScore && !isReviewMode
      ? metricScore?.weightedScore
      : calculatedWeightedScore;

  // Expose validate() and getValues() to parent via ref
  useImperativeHandle(ref, () => ({
    validate: async () => {
      const errors = await formik.validateForm();
      formik.setTouched({
        score: true,
        reason: true,
        areasOfImprovement: true,
      });
      return Object.keys(errors).length === 0;
    },
    getValues: () => ({
      metricId: score.metric.id,
      ...formik.values,
    }),
  }));

  const handleFieldChange = (
    field: "score" | "reason" | "areasOfImprovement",
    value: number | string,
  ) => {
    formik.setFieldValue(field, value);
  };

  return (
    <div className="bg-white border border-border rounded-md overflow-hidden shrink-0">
      {/* Metric header */}
      <div className="flex items-center justify-between p-3.5 bg-violet-50/50 border-b">
        <span className="text-xs font-semibold text-gray-600">
          {score.metric.title}
        </span>
        <span className="text-xs bg-slate-50 p-1 rounded-xxs text-gray-500">
          <Trans
            t={t}
            i18nKey="company:performance_review.weightage_value"
            values={{ value: score.weightage }}
            components={{ value: <span className="text-black" /> }}
          />
        </span>
      </div>

      <div
        className={cn(
          "bg-violet-100 border border-gray-100",
          isReviewMode && "bg-white",
        )}
      >
        {/* Question + Emoji */}
        <div className="px-4 pt-4 pb-3">
          <p className="text-sm font-medium text-gray-800 mb-3 flex gap-1.5">
            <span className="text-gray-400 font-normal">
              {t("company:performance_review.question_number", {
                number: index,
              })}
            </span>
            {score.metric.description}
          </p>
          <EmojiRating
            selected={formik.values.score}
            isReviewMode={isReviewMode}
            onChange={
              isReviewMode
                ? (val) => handleFieldChange("score", val)
                : undefined
            }
          />
          {formik.touched.score && formik.errors.score && (
            <p className="text-xs text-red-500 mt-1">{formik.errors.score}</p>
          )}
        </div>

        {/* Reason */}
        <div className="mx-4 mb-2">
          {isReviewMode ? (
            <>
              <TextField
                type="text"
                label={t("common:shared_ui.reason")}
                name="reason"
                formik={formik}
                placeholder={t("common:placeholders.enter_reason")}
              />
            </>
          ) : (
            <div className="rounded-xs border bg-gray-50/50 px-3 py-2">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
                {t("company:performance_review.reason_colon")}
              </p>
              <p className="text-sm text-gray-700">
                {formik.values.reason || "-"}
              </p>
            </div>
          )}
        </div>

        {/* Area of Improvement */}
        <div className="mx-4 mb-4">
          {isReviewMode ? (
            <>
              <TextField
                type="text"
                label={t("company:performance_review.area_of_improvement")}
                name="areasOfImprovement"
                formik={formik}
                placeholder={t("common:placeholders.enter_area_of_improvement")}
              />
            </>
          ) : (
            <div className="rounded-xs border bg-gray-50/50 px-3 py-2">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
                {t("company:performance_review.area_of_improvement_colon")}
              </p>
              <p className="text-sm text-gray-700">
                {formik.values.areasOfImprovement || "-"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Metric score footer */}
      <div className="flex items-center justify-end px-4 py-3 border-t bg-white border-gray-100">
        <span className="text-xs text-gray-500">
          <Trans
            t={t}
            i18nKey="company:performance_review.metric_score_value"
            values={{ value: displayScore || "-" }}
            components={{ value: <span className="text-black font-semibold" /> }}
          />
        </span>
      </div>
    </div>
  );
};
